import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import {
  Search, Loader2, Building2, TrendingUp, Newspaper, X,
  ArrowUpRight, Rocket, Compass, Flame
} from "lucide-react";
import api from "../../lib/api";

const SECTIONS = [
  {
    title: "Directory",
    color: "text-indigo-600",
    bgColor: "bg-indigo-50/50",
    gradient: "from-indigo-500/10 to-pink-500/5",
    items: [
      { label: "Startups", desc: "High-growth tech ventures in India", icon: Rocket, to: "/discover/startups" },
      { label: "Incubators", desc: "Accelerators and campus incubator programs", icon: Building2, to: "/discover/incubators" },
      { label: "VC Firms", desc: "Venture funds backing local founders", icon: TrendingUp, to: "/discover/vc-firms" },
    ],
  },
  {
    title: "Insights",
    color: "text-[#EC4899]",
    bgColor: "bg-pink-50/50",
    gradient: "from-pink-500/10 to-orange-500/5",
    items: [
      { label: "Trending Startups", desc: "Breakout month-on-month growth metrics", icon: Flame, to: "/discover/trending-startups" },
    ],
  },
  {
    title: "Resources",
    color: "text-[#A855F7]",
    bgColor: "bg-purple-50/50",
    gradient: "from-purple-500/10 to-indigo-500/5",
    items: [
      { label: "Explore Industries", desc: "Browse ventures categorized by sector", icon: Compass, to: "/discover/industries" },
    ],
  },
];

function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function CompanyLogo({ logo, name }) {
  const [err, setErr] = useState(false);
  if (!logo || err) {
    return (
      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white text-xs font-display font-semibold flex items-center justify-center shrink-0">
        {(name || "?").trim()[0].toUpperCase()}
      </div>
    );
  }
  return (
    <img src={logo} alt={`${name} logo`} onError={() => setErr(true)}
         className="w-9 h-9 rounded-xl object-contain bg-white border border-gray-100 shrink-0" />
  );
}

function Skeleton({ rows = 4 }) {
  return (
    <div className="space-y-3 animate-pulse" aria-hidden>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gray-100" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-2/3 bg-gray-100 rounded" />
            <div className="h-2.5 w-1/2 bg-gray-100 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function DiscoverMegaMenu({ onClose, panelId = "discover-mega", isMobile = false, onMouseEnter, onMouseLeave }) {
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 250);
  const panelRef = useRef(null);
  const routerNav = useNavigate();

  // Search queries from database
  const { data: searchResults = null, isLoading: searching } = useQuery({
    queryKey: ["discover-search", dq],
    queryFn: () => {
      if (!dq.trim()) return Promise.resolve(null);
      return api.get(`/discover/search?q=${encodeURIComponent(dq)}&limit=12`).then((r) => r.data.items || []);
    },
    enabled: dq.trim().length > 0,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  // ESC key and focus traps
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose?.();
        return;
      }
      if (!panelRef.current) return;
      
      const focusables = Array.from(
        panelRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex="0"]'
        )
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      
      if (e.key === "Tab") {
        if (e.shiftKey) {
          if (document.activeElement === first) {
            last.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === last) {
            first.focus();
            e.preventDefault();
          }
        }
      }
    };
    
    document.addEventListener("keydown", handleKeyDown);
    const id = setTimeout(() => panelRef.current?.querySelector('input[type="search"]')?.focus(), 80);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      clearTimeout(id);
    };
  }, [onClose]);

  const navigate = useCallback((url, external = false) => {
    if (external) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      routerNav(url);
    }
    onClose?.();
  }, [onClose, routerNav]);

  const handleBackdropClose = (e) => {
    if (panelRef.current && e.relatedTarget && !panelRef.current.contains(e.relatedTarget)) {
      onClose?.();
    }
  };

  return (
    <motion.div
      ref={panelRef}
      id={panelId}
      role="dialog"
      aria-modal="true"
      aria-label="Discover Menu Hub"
      initial={isMobile ? { x: "100%" } : { opacity: 0, y: -8, x: "-50%" }}
      animate={isMobile ? { x: 0 } : { opacity: 1, y: 0, x: "-50%" }}
      exit={isMobile ? { x: "100%" } : { opacity: 0, y: -8, x: "-50%" }}
      transition={{ type: "spring", damping: 25, stiffness: 220 }}
      className={
        isMobile
          ? "fixed inset-0 z-[9999] w-screen h-screen bg-white text-gray-900 overflow-y-auto flex flex-col focus:outline-none"
          : "fixed left-1/2 top-[80px] z-[9999] w-[min(95vw,1000px)] max-h-[85vh] overflow-y-auto rounded-[24px] bg-white text-gray-900 border border-gray-200 shadow-[0_30px_90px_rgba(0,0,0,0.3)] flex flex-col focus:outline-none"
      }
      onMouseEnter={!isMobile ? onMouseEnter : undefined}
      onMouseLeave={!isMobile ? (e) => { handleBackdropClose(e); onMouseLeave?.(e); } : undefined}
      data-testid="discover-megamenu"
    >
      {/* Search Header */}
      <div className="px-8 pt-7 pb-4 border-b border-gray-100 flex items-center gap-3">
        <label className="block relative flex-1">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search startups, investors, incubators, news..."
            aria-label="Search Ecosystem"
            className="w-full pl-11 pr-10 py-3.5 rounded-2xl bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 outline-none text-sm transition-all font-medium"
            data-testid="discover-search"
          />
          {q && (
            <button onClick={() => setQ("")} aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 text-gray-400">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </label>
        {isMobile && (
          <button
            onClick={onClose}
            aria-label="Close menu"
            className="p-3 rounded-full hover:bg-gray-100 text-gray-500 transition"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Search results override */}
      {q.trim() ? (
        <div className="px-8 py-6 flex-1 overflow-y-auto">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-4">
            {searching ? "Searching Directory…" : `Results for "${q}"`}
          </p>
          {searching ? <Skeleton rows={6} /> : (
            searchResults && searchResults.length > 0 ? (
              <div className="space-y-6">
                {Object.entries(
                  searchResults.reduce((acc, item) => {
                    const t = item.type || "startup";
                    acc[t] = acc[t] || [];
                    acc[t].push(item);
                    return acc;
                  }, {})
                ).map(([type, list]) => {
                  const typeNames = {
                    startup: "Startups",
                    incubator: "Incubators & Accelerators",
                    vc: "VC Firms",
                    industry: "Industries",
                    news: "News & Funding"
                  };
                  return (
                    <div key={type} className="space-y-2.5">
                      <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{typeNames[type] || type}</h4>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {list.map((c, idx) => (
                          <li key={`${c.slug}-${idx}`}>
                            <button
                              onClick={() => {
                                if (c.type === "industry") {
                                  navigate(`/discover/startups?industry=${c.slug}`);
                                } else if (c.type === "news") {
                                  navigate(c.website || c.url, true);
                                } else {
                                  navigate(`/discover/company/${c.slug}`);
                                }
                              }}
                              className="w-full flex items-center gap-3 px-3.5 py-3 rounded-2xl hover:bg-gray-50 text-left transition group border border-gray-50 hover:border-gray-100 hover:shadow-sm"
                            >
                              {c.logo ? (
                                <CompanyLogo logo={c.logo} name={c.name} />
                              ) : (
                                <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-400 shrink-0">
                                  {c.type === "industry" ? <Compass className="w-4 h-4" /> : <Newspaper className="w-4 h-4" />}
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold truncate text-gray-900 group-hover:text-[#7C3AED]">{c.name}</p>
                                <p className="text-xs text-gray-500 truncate leading-relaxed">{c.description || c.industry}</p>
                              </div>
                              <ArrowUpRight className="w-4 h-4 text-gray-300 group-hover:text-[#7C3AED] transition" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-gray-500">No matching ecosystem entries found. Try searching for a different company or sector.</p>
            )
          )}
        </div>
      ) : (
        /* Organized Sections */
        <div className="flex-1 p-8 grid grid-cols-1 md:grid-cols-3 gap-6 overflow-y-auto bg-white">
          {SECTIONS.map((sec) => (
            <div key={sec.title} className="space-y-4">
              <h3 className={`text-[11px] font-extrabold uppercase tracking-widest ${sec.color} border-b border-gray-100 pb-2`}>
                {sec.title}
              </h3>
              <ul className="space-y-2.5">
                {sec.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.label}>
                      <button
                        onClick={() => navigate(item.to)}
                        className="w-full flex items-start gap-3 p-2.5 rounded-2xl hover:bg-gray-50 border border-transparent hover:border-gray-100/65 hover:shadow-sm text-left group transition-all duration-200"
                        data-testid={`menu-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <div className={`p-2 rounded-xl bg-gray-50 text-gray-400 group-hover:bg-[#7C3AED]/10 group-hover:text-[#7C3AED] transition-colors shrink-0`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-gray-900 group-hover:text-[#7C3AED] transition-colors">
                            {item.label}
                          </p>
                          <p className="text-[10px] text-gray-400 leading-normal font-normal mt-0.5 max-w-[200px]">
                            {item.desc}
                          </p>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Footer Quick Portal Link */}
      <div className="border-t border-gray-100 px-8 py-4 bg-white flex items-center justify-between shrink-0">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
          STUDLYF AI Ecosystem Portal
        </span>
        <button
          onClick={() => navigate("/discover")}
          className="text-xs font-bold text-[#7C3AED] hover:underline flex items-center gap-0.5"
        >
          Open Portal Hub <ArrowUpRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
}
